import { useQuery } from "@tanstack/react-query";
import { useAccessToken } from "@/hooks";
import { PrivatesAppointmentsApi } from "@/openapi/generated";
import type { AppointmentScrollQuery } from "@/openapi/generated/models";
import { QueryKeys } from "@/lib/types";
import { createAuthenticatedApi } from "@/lib/utils";

interface IUseAppointmentsGetProps {
  appointmentScrollQuery?: AppointmentScrollQuery;
  page?: number;
  pageSize?: number;
  lastSelectedId?: number;
  active?: boolean;
  enabled?: boolean;
  refetchOnMount?: boolean | "always";
}

export const useAppointmentsGet = ({
  appointmentScrollQuery,
  page = 1,
  pageSize = 100,
  lastSelectedId,
  active,
  enabled = true,
  refetchOnMount = false,
}: IUseAppointmentsGetProps = {}) => {
  const accessToken = useAccessToken();

  const {
    data,
    isError,
    isLoading,
    isSuccess,
    isPending,
    refetch,
    isFetching,
    error,
  } = useQuery({
    queryKey: [
      QueryKeys.Appointments,
      appointmentScrollQuery,
      page,
      pageSize,
      lastSelectedId,
      active,
    ],
    queryFn: () => {
      if (!accessToken) {
        throw new Error("Authentication token is required");
      }
      const service = createAuthenticatedApi(
        PrivatesAppointmentsApi,
        accessToken,
      );

      const query = appointmentScrollQuery || ({} as AppointmentScrollQuery);

      return service.apiAppointmentsScrollPost({
        appointmentScrollQuery: query,
        page,
        pageSize,
        lastSelectedId,
        active,
      });
    },
    enabled: !!accessToken && enabled,
    refetchOnMount,
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000,
  });

  return {
    data,
    isError,
    isLoading,
    isSuccess,
    isPending,
    refetch,
    isFetching,
    error,
  };
};
